import { Form, Input, Button, Row, Col, Space, Select, Modal } from "antd";

import {
  CopyOutlined,
  ReloadOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { Sparkles, Landmark } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { FetchBankAccounts_api } from "../../Api/Bank/bank_api";
import { trimPriceAmount } from "../../lib/common";

const { Option } = Select;

const SlipForm = ({
  maximumAmount,
  formData,
  isAdding,
  handleSubmit,
  handleReset,
  copyToClipboard,
  handleFile,
  imageMetaData,
  isLoading,
}: any) => {
  const fields = [
    "slip_id",
    "transfer_id",
    "issued_date",
    "time",
    "amount",
    "slip_bank_name",
    "beneficiary_bank_name",
    "branch_name",
    "account_number",
    "depositor_name",
  ];

  const { data: bankAccounts, isFetching: isFetchingBanks } = useQuery({
    queryKey: ["bankAccounts"],
    queryFn: async () => {
      const res = await FetchBankAccounts_api();
      return res?.data || [];
    },
    refetchOnWindowFocus: false,
  });

  const amountValue = Form.useWatch("amount", formData);
  const bankAccId = Form.useWatch("bank_account_id", formData);

  const requiredFields = [
    "transfer_id",
    "issued_date",
    "time",
    "amount",
    "slip_bank_name",
    "bank_account_id",
  ];

  return (
    <Form
      form={formData}
      layout="vertical"
      onFinish={(value) => {
        Modal.confirm({
          title: "Confirm Submission",
          content:
            "As this process directly affects financial records, it is irreversible. Please carefully review and verify all entered data before proceeding.",
          okText: "Confirm",
          cancelText: "Cancel",
          okButtonProps: { danger: true },
          onOk: () => {
            handleSubmit(value);
          },
        });
      }}
      onLoad={isAdding}
      initialValues={formData}
      className="p-4 rounded-lg"
    >
      <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 mb-6 flex items-start gap-3">
        <Landmark className="text-blue-500 w-5 h-5 mt-1" />
        <div className="flex-1">
          <Form.Item
            label={
              <span className="font-semibold text-blue-700">
                Deposited to (Bank Account)
              </span>
            }
            name="bank_account_id"
            rules={[
              {
                required: true,
                message: "Please select the destination bank account",
              },
            ]}
            className="mb-0"
          >
            <Select
              placeholder="-- Select Internal Bank Account --"
              loading={isFetchingBanks}
              className="w-full"
            >
              {bankAccounts
                ?.filter((a: any) => a.isOwn == 1)
                .map((acc: any) => (
                  <Option key={acc.id} value={acc.id}>
                    {acc.bank_name} - {acc.account_name} ({acc.account_number})
                  </Option>
                ))}
            </Select>
          </Form.Item>
        </div>
      </div>

      <Row gutter={[16, 16]}>
        {fields.map((field: any) => {
          const isRequired = requiredFields.includes(field);
          const label = field.replaceAll("_", " ");
          const isAmount = field === "amount";
          const maximumAmountRule = isAmount
            ? [
                {
                  validator: (_: any, value: any) => {
                    if (value && Number(value) > maximumAmount) {
                      return Promise.reject(
                        `Amount cannot be greater than ${maximumAmount}`,
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]
            : [];

          return (
            <Col xs={24} md={12} key={field}>
              <Form.Item
                className="my-0"
                label={label}
                name={field}
                normalize={
                  isAmount
                    ? (value) =>
                        trimPriceAmount(
                          Number(value.toString().replace(/,/g, "")),
                        )
                    : undefined
                }
                rules={
                  isRequired
                    ? [
                        {
                          required: true,
                          message: `Please enter ${label}`,
                        },
                        ...maximumAmountRule,
                      ]
                    : []
                }
              >
                <Input
                  placeholder={`Enter ${label}`}
                  name={field}
                  value={formData[field]}
                />
              </Form.Item>
            </Col>
          );
        })}
      </Row>

      {/* Other IDs */}
      {formData.other_ids?.length > 0 && (
        <Form.Item label="Other IDs" className="mt-5">
          <Space wrap>
            {formData.other_ids.map((id: string, idx: number) => (
              <Button
                key={idx}
                type="default"
                onClick={() => copyToClipboard(id)}
                icon={<CopyOutlined />}
              >
                {id}
              </Button>
            ))}
          </Space>
        </Form.Item>
      )}

      <Form.Item>
        <Space className="w-full justify-between flex mt-16">
          <Button
            loading={isLoading}
            className="flex items-center gap-2 rounded-2xl bg-gradient-to-r from-blue-600 via-cyan-500 to-sky-400 px-6 py-3 text-white font-semibold shadow-md transition-all duration-300 hover:from-cyan-500 hover:to-blue-600"
            htmlType="button"
            disabled={!imageMetaData}
            onClick={() => {
              handleFile(imageMetaData);
            }}
            icon={<Sparkles />}
          >
            Re annelize
          </Button>

          <div className="flex space-x-2">
            <Button
              disabled={isLoading}
              htmlType="button"
              onClick={handleReset}
              icon={<ReloadOutlined />}
            >
              Reset
            </Button>
            <Button
              disabled={
                isLoading ||
                maximumAmount === 0 ||
                (amountValue && Number(amountValue) > maximumAmount) ||
                !bankAccId
              }
              type="primary"
              htmlType="submit"
              loading={isAdding}
              icon={isAdding ? <LoadingOutlined /> : undefined}
            >
              Submit
            </Button>
          </div>
        </Space>
      </Form.Item>
    </Form>
  );
};

export default SlipForm;
